import { MyNotificationsViewModel } from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, OnInit, OnDestroy, ViewChild, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription, combineLatest, finalize } from 'rxjs';
import { NotificationService } from '@/app/core/services/notification.service';
import { Popover } from 'primeng/popover';
import { DkActionButtonProps } from '@/app/shared/components/dk-button/dk-button.component';
import { TranslateService } from '@ngx-translate/core';

interface DayBasedNotificationGroup {
  date: DateTime;
  notifications: MyNotificationsViewModel[];
}

@Component({
  selector: 'dk-notifications',
  imports: [SharedModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.less',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('notificationActionsPopover') notificationActionsPopover!: Popover;
  @Output() popoverStateChange = new EventEmitter<boolean>();

  drawerVisible = false;
  notifications: MyNotificationsViewModel[] = [];
  notificationGroups: DayBasedNotificationGroup[] = [];
  unreadCount = 0;
  isLoading = false;
  showOnlyUnread = false;

  get notificationActions(): DkActionButtonProps[] {
    return [
      {
        labelKey: 'doktar_app.account.notifications.mark_all_as_read',
        color: '',
        isDisabled: this.unreadCount === 0,
        onClick: () => this.markAllAsSeen(),
      },
      {
        labelKey: this.showOnlyUnread
          ? 'doktar_app.account.notifications.show_all'
          : 'doktar_app.account.notifications.show_only_unread',
        color: '',
        isDisabled: false,
        onClick: () => this.toggleUnreadFilter(),
      },
    ];
  }

  private subscription?: Subscription;
  private failedIconIds = new Set<string>();

  constructor(
    private notificationService: NotificationService,
    private _translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeNotifications();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Initialize notifications with optimized reactive flow
   */
  private initializeNotifications(): void {
    this.setupReactiveSubscriptions();
    this.loadNotificationsWithCacheStrategy();
  }

  /**
   * Set up optimized reactive subscriptions using combineLatest
   */
  private setupReactiveSubscriptions(): void {
    this.subscription = combineLatest([
      this.notificationService.notifications$,
      this.notificationService.unreadCount$,
    ]).subscribe(([notifications, unreadCount]) => {
      this.notifications = notifications;
      this.unreadCount = unreadCount;
      this.applyDisplayedGroups();
    });
  }

  /**
   * Load notifications with smart caching strategy: cached first, then fresh
   */
  private loadNotificationsWithCacheStrategy(): void {
    this.loadCachedNotificationsForInstantDisplay();
    this.fetchFreshNotificationsFromAPI();
  }

  /**
   * Load cached notifications for instant display
   */
  private loadCachedNotificationsForInstantDisplay(): void {
    const cachedNotifications = this.notificationService.getCachedNotifications();
    if (cachedNotifications.length > 0) {
      this.notifications = cachedNotifications;
      this.applyDisplayedGroups();
    }
  }

  /**
   * Fetch fresh notifications from API with proper loading state management
   */
  private fetchFreshNotificationsFromAPI(): void {
    this.isLoading = true;

    this.notificationService
      .fetchNotifications()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.applyDisplayedGroups();
        },
        error: () => {},
      });
  }

  /**
   * Public method to refresh notifications (triggered by UI actions)
   */
  public refreshNotifications(): void {
    this.isLoading = true;
    this.notificationService
      .refreshNotifications()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {},
      });
  }

  private applyDisplayedGroups(): void {
    const source = this.showOnlyUnread
      ? this.notifications.filter((notification) => !notification.isSeen)
      : this.notifications;
    this.groupByDaily(source);
  }

  /**
   * Group notifications by daily basis with optimized sorting
   */
  private groupByDaily(notifications: MyNotificationsViewModel[]): void {
    if (!notifications || notifications.length === 0) {
      this.notificationGroups = [];
      return;
    }

    const groups: DayBasedNotificationGroup[] = [];

    notifications.forEach((notification) => {
      const date = DateTime.fromJSDate(notification.createdAt.toJSDate()).startOf('day');
      const group = groups.find((g) => g.date.equals(date));

      if (group) {
        group.notifications.push(notification);
      } else {
        groups.push({
          date: date,
          notifications: [notification],
        });
      }
    });

    // Sort groups by date (latest first) and notifications within each group by createdAt (latest first)
    this.notificationGroups = groups
      .sort((a, b) => b.date.diff(a.date).toMillis())
      .map((group) => ({
        ...group,
        notifications: group.notifications.sort(
          (a, b) => b.createdAt.toJSDate().getTime() - a.createdAt.toJSDate().getTime()
        ),
      }));
  }

  /**
   * Handle notification click - open URL and mark as seen
   */
  public openNotificationUrl(notification: MyNotificationsViewModel): void {
    if (!notification.isSeen) {
      this.markAsSeen(notification);
    }
    if (notification.url) {
      window.open(notification.url, '_self');
    }
  }

  public openActionsPanel(event: Event): void {
    if (this.notificationActionsPopover) {
      this.notificationActionsPopover.toggle(event);
    }
  }

  public onPopoverShow(): void {
    this.popoverStateChange.emit(true);
  }

  public onPopoverHide(): void {
    this.popoverStateChange.emit(false);
  }

  public toggleUnreadFilter(): void {
    this.showOnlyUnread = !this.showOnlyUnread;
    this.applyDisplayedGroups();
    this.notificationActionsPopover?.hide();
  }

  public showAllNotifications(): void {
    this.showOnlyUnread = false;
    this.applyDisplayedGroups();
    this.notificationActionsPopover?.hide();
  }

  public showOnlyUnreadNotifications(): void {
    this.showOnlyUnread = true;
    this.applyDisplayedGroups();
    this.notificationActionsPopover?.hide();
  }

  /**
   * Mark a single notification as seen with error handling
   */
  public markAsSeen(notification: MyNotificationsViewModel): void {
    if (!notification.isSeen) {
      this.notificationService.markAsSeen(notification).subscribe();
    }
  }

  /**
   * Mark all notifications as seen with error handling
   */
  public markAllAsSeen(): void {
    this.notificationService.markAllAsSeen().subscribe({
      next: () => {
        this.notificationActionsPopover?.hide();
      },
    });
  }

  public onNotificationIconError(notificationId?: string): void {
    if (notificationId) {
      this.failedIconIds.add(notificationId);
      this.cdr.detectChanges();
    }
  }

  public hasIconError(notificationId?: string): boolean {
    return !!notificationId && this.failedIconIds.has(notificationId);
  }

  /**
   * Get formatted date header for notification groups with smart formatting
   */
  public getNotificationGroupDateHeader(date: DateTime): string {
    const now = DateTime.now();

    if (date.hasSame(now, 'day')) {
      return this._translateService.instant('doktar_app.common.today');
    } else if (date.hasSame(now.minus({ days: 1 }), 'day')) {
      return this._translateService.instant('doktar_app.common.yesterday');
    } else {
      const locale = localStorage.getItem('languageISO1') || 'en';
      return date.setLocale(locale).toFormat('d MMMM');
    }
  }
}
